import React, { useState, useCallback, useRef, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import apiClient from '../../api/client';
import ProjectMatchBanner, { ProjectMatch } from '../../components/search/ProjectMatchBanner';
import PropertiesTab from '../../components/chemistry/compound-tabs/PropertiesTab';
import PatentsTab from '../../components/chemistry/compound-tabs/PatentsTab';
import TargetsTab from '../../components/chemistry/compound-tabs/TargetsTab';
import PathwaysTab from '../../components/chemistry/compound-tabs/PathwaysTab';
import ReactionsTab from '../../components/chemistry/compound-tabs/ReactionsTab';
import BioactivityTab from '../../components/chemistry/compound-tabs/BioactivityTab';
import ThreeDTab from '../../components/chemistry/compound-tabs/ThreeDTab';
import { StructureEditorModal } from '../../components/chemistry/StructureEditor';
import StructureImage from '../../components/chemistry/StructureImage';

const SpeechRecognition = typeof window !== 'undefined'
  ? (window as any).SpeechRecognition || (window as any).webkitSpeechRecognition
  : null;

type DetailTab = 'properties' | 'patents' | 'targets' | 'pathways' | 'reactions' | 'bioactivity' | '3d';
type SearchMode = 'substructure' | 'exact' | 'similarity';

const TABS: { id: DetailTab; label: string }[] = [
  { id: 'properties', label: 'Properties' },
  { id: 'patents', label: 'Patents' },
  { id: 'targets', label: 'Targets' },
  { id: 'pathways', label: 'Pathways' },
  { id: 'reactions', label: 'Reactions' },
  { id: 'bioactivity', label: 'Bioactivity' },
  { id: '3d', label: '3D' },
];

/** Heuristic: does the input look like a SMILES string? */
function looksLikeSMILES(input: string): boolean {
  // SMILES strings typically contain lowercase letters (c, n, o for aromatic),
  // brackets, parentheses, digits for rings, =, #, etc.
  // Pure compound names are usually just letters and spaces/hyphens.
  const trimmed = input.trim();
  if (!trimmed) return false;
  // If it contains spaces, it's likely a name (unless it's a very unusual SMILES)
  if (/\s/.test(trimmed)) return false;
  // If it contains typical SMILES characters, it's likely SMILES
  if (/[()=#\[\]@\\\/]/.test(trimmed)) return true;
  // If it has lowercase letters mixed with uppercase (like 'CCO', 'c1ccccc1')
  if (/[a-z]/.test(trimmed) && /[A-Z0-9]/.test(trimmed)) return true;
  // If it starts with a ring digit or element symbol followed by typical SMILES chars
  if (/^[A-Z][a-z]?[0-9(=]/.test(trimmed)) return true;
  // Otherwise treat as a name
  return false;
}

const COMMON_COMPOUNDS = [
  'aspirin', 'ibuprofen', 'caffeine', 'morphine', 'paracetamol', 'benzene',
  'toluene', 'acetaminophen', 'naproxen', 'diazepam', 'omeprazole', 'metformin',
  'atorvastatin', 'amoxicillin', 'ciprofloxacin', 'warfarin', 'insulin',
  'penicillin', 'celecoxib', 'losartan', 'sildenafil', 'tamoxifen',
  'dexamethasone', 'prednisone', 'lidocaine', 'propranolol', 'ranitidine',
];

const QuickSearchPage: React.FC = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const [query, setQuery] = useState('');
  const [activeTab, setActiveTab] = useState<DetailTab>('properties');
  const [suggestions, setSuggestions] = useState<string[]>([]);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const suggestTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const suggestionsRef = useRef<HTMLDivElement>(null);
  const [molecule, setMolecule] = useState<{
    smiles: string;
    properties: Record<string, any>;
  } | null>(null);
  const [svgContent, setSvgContent] = useState<string | null>(null);
  const [isSearching, setIsSearching] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [resolvedName, setResolvedName] = useState<string | null>(null);
  const [editorOpen, setEditorOpen] = useState(false);
  const [drawEditorOpen, setDrawEditorOpen] = useState(false);
  const [isListening, setIsListening] = useState(false);
  const [didYouMean, setDidYouMean] = useState<{ name: string; smiles: string } | null>(null);
  const [searchMode, setSearchMode] = useState<SearchMode>('substructure');
  const [similarityThreshold, setSimilarityThreshold] = useState(0.7);
  const [projectMatches, setProjectMatches] = useState<ProjectMatch[]>([]);
  const [projectMatchesLoading, setProjectMatchesLoading] = useState(false);
  const [reportQuery, setReportQuery] = useState<string>('');
  const [isDownloadingPdf, setIsDownloadingPdf] = useState(false);
  const [pdfError, setPdfError] = useState<string | null>(null);
  const recognitionRef = useRef<any>(null);
  const autoSearchDoneRef = useRef(false);
  const findInProjectsAbortRef = useRef<AbortController | null>(null);

  const searchWithSmiles = useCallback(async (smiles: string) => {
    const summaryResp = await apiClient.post('/search/summary', { smiles });
    const summary = summaryResp.data;
    const canonicalSmiles = summary.isomeric_smiles || summary.canonical_smiles || smiles;

    const renderResp = await apiClient.post('/v2/indigo/render', {
      structure: { value: canonicalSmiles, type: 'RAW' },
      width: '380px',
      height: '200px',
    }, { responseType: 'text' });
    setSvgContent(renderResp.data);

    setMolecule({
      smiles: canonicalSmiles,
      properties: summary,
    });
    setActiveTab('properties');
    setPdfError(null);

    // Search user's own projects for this compound (non-blocking)
    // Cancel any previous in-flight request to prevent pileup
    findInProjectsAbortRef.current?.abort();
    const abortCtrl = new AbortController();
    findInProjectsAbortRef.current = abortCtrl;
    setProjectMatchesLoading(true);
    setProjectMatches([]);
    const findParams: Record<string, any> = { smiles: canonicalSmiles, search_type: searchMode };
    if (searchMode === 'similarity') findParams.threshold = similarityThreshold;
    apiClient.get('/search/find-in-projects', { params: findParams, signal: abortCtrl.signal })
      .then((resp) => {
        setProjectMatches(resp.data.matches || []);
      })
      .catch((err) => {
        if (err?.name !== 'CanceledError' && err?.code !== 'ERR_CANCELED') {
          setProjectMatches([]);
        }
      })
      .finally(() => {
        setProjectMatchesLoading(false);
      });
  }, [searchMode, similarityThreshold]);

  const downloadPdfSummary = useCallback(async () => {
    if (!molecule) return;
    setIsDownloadingPdf(true);
    setPdfError(null);
    try {
      const response = await apiClient.post('/search/summary-pdf', {
        smiles: molecule.smiles,
        query: resolvedName || reportQuery || undefined,
      }, { responseType: 'blob' });
      const disposition = response.headers['content-disposition'] || '';
      const filenameMatch = disposition.match(/filename="?([^";]+)"?/i);
      const filename = filenameMatch?.[1] || 'adroit-compound-summary.pdf';
      const url = window.URL.createObjectURL(new Blob([response.data], { type: 'application/pdf' }));
      const link = document.createElement('a');
      link.href = url;
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (err: any) {
      setPdfError(err.response?.data?.detail || 'Could not create the PDF summary');
    } finally {
      setIsDownloadingPdf(false);
    }
  }, [molecule, reportQuery, resolvedName]);

  const handleSearch = useCallback(async () => {
    const trimmed = query.trim();
    if (!trimmed) return;

    // Persist query in URL so browser Back restores it
    setSearchParams({ q: trimmed }, { replace: true });
    sessionStorage.setItem('discoverant_quick_search_last', trimmed);

    setIsSearching(true);
    setReportQuery(trimmed);
    setError(null);
    setSvgContent(null);
    setResolvedName(null);
    setDidYouMean(null);
    setProjectMatches([]);
    setProjectMatchesLoading(false);

    try {
      if (looksLikeSMILES(trimmed)) {
        // Direct SMILES search
        await searchWithSmiles(trimmed);
      } else {
        // Private internal codes take precedence over public name resolution.
        try {
          try {
            const internal = await apiClient.get('/search/internal-code', { params: { code: trimmed } });
            if (internal.data?.smiles) {
              setResolvedName(internal.data.internal_code || trimmed);
              setQuery(internal.data.smiles);
              await searchWithSmiles(internal.data.smiles);
              return;
            }
          } catch (internalErr: any) {
            if (internalErr?.response?.status !== 404) throw internalErr;
          }
          const nameResp = await apiClient.get('/search/resolve-name', {
            params: { name: trimmed },
          });
          const { smiles, name, fuzzy, error: resolveError } = nameResp.data;
          if (smiles && !fuzzy) {
            // Exact match
            setResolvedName(name);
            setQuery(smiles);
            await searchWithSmiles(smiles);
          } else if (smiles && fuzzy) {
            // Fuzzy match — show chip, don't auto-search
            setDidYouMean({ name, smiles });
            setIsSearching(false);
          } else if (resolveError === 'not_found') {
            setError(`Could not resolve '${trimmed}' to a structure. Try pasting a SMILES string instead.`);
            setMolecule(null);
          } else {
            setError(`Could not resolve '${trimmed}' to a structure. Try pasting a SMILES string instead.`);
            setMolecule(null);
          }
        } catch (nameErr: any) {
          if (nameErr.response?.status === 404) {
            setError(`Could not resolve '${trimmed}' to a structure. Try pasting a SMILES string instead.`);
            setMolecule(null);
          } else {
            // Maybe it's actually a SMILES that just looks like a name - try anyway
            try {
              await searchWithSmiles(trimmed);
            } catch {
              setError(`Could not resolve '${trimmed}' to a structure. Try pasting a SMILES string instead.`);
              setMolecule(null);
            }
          }
        }
      }
    } catch (err: any) {
      setError(err.response?.data?.detail || err.message || 'Invalid structure');
      setMolecule(null);
    } finally {
      setIsSearching(false);
    }
  }, [query, searchWithSmiles, setSearchParams]);

  // Restore search on mount: URL ?q= first, then sessionStorage fallback
  useEffect(() => {
    if (autoSearchDoneRef.current) return;
    const q = searchParams.get('q') || sessionStorage.getItem('discoverant_quick_search_last');
    if (!q) return;
    autoSearchDoneRef.current = true;
    setQuery(q);
    // Sync URL if restoring from sessionStorage
    if (!searchParams.get('q')) {
      setSearchParams({ q }, { replace: true });
    }

    const run = async () => {
      const trimmed = q.trim();
      if (!trimmed) return;
      setIsSearching(true);
      setError(null);
      try {
        if (looksLikeSMILES(trimmed)) {
          await searchWithSmiles(trimmed);
        } else {
          const nameResp = await apiClient.get('/search/resolve-name', {
            params: { name: trimmed },
          });
          const { smiles, name, fuzzy } = nameResp.data;
          if (smiles && !fuzzy) {
            setResolvedName(name);
            setQuery(smiles);
            await searchWithSmiles(smiles);
          } else if (smiles && fuzzy) {
            setDidYouMean({ name, smiles });
          }
        }
      } catch {
        // Silently fail on restore — user can re-search manually
      } finally {
        setIsSearching(false);
      }
    };
    run();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const handleDrawSave = useCallback(async (_molfile: string, smiles: string) => {
    setDrawEditorOpen(false);
    if (smiles) {
      setQuery(smiles);
      setSearchParams({ q: smiles }, { replace: true });
      sessionStorage.setItem('discoverant_quick_search_last', smiles);
      setIsSearching(true);
      setError(null);
      setSvgContent(null);
      setResolvedName(null);
      try {
        await searchWithSmiles(smiles);
      } catch (err: any) {
        setError(err.response?.data?.detail || err.message || 'Invalid structure');
        setMolecule(null);
      } finally {
        setIsSearching(false);
      }
    }
  }, [searchWithSmiles, setSearchParams]);

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') { setShowSuggestions(false); handleSearch(); }
    if (e.key === 'Escape') setShowSuggestions(false);
  };

  // Debounced autocomplete suggestions
  const handleInputChange = (value: string) => {
    setQuery(value);
    if (suggestTimer.current) clearTimeout(suggestTimer.current);
    const trimmed = value.trim().toLowerCase();
    if (trimmed.length < 2 || looksLikeSMILES(value.trim())) {
      setSuggestions([]);
      setShowSuggestions(false);
      return;
    }
    suggestTimer.current = setTimeout(() => {
      const local = COMMON_COMPOUNDS.filter(n => n.startsWith(trimmed)).slice(0, 8);
      setSuggestions(local);
      setShowSuggestions(local.length > 0);
    }, 300);
  };

  const selectSuggestion = (name: string) => {
    setQuery(name);
    setShowSuggestions(false);
    setSuggestions([]);
  };

  // Close suggestions on outside click
  useEffect(() => {
    const handleClick = (e: MouseEvent) => {
      if (suggestionsRef.current && !suggestionsRef.current.contains(e.target as Node)) {
        setShowSuggestions(false);
      }
    };
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, []);

  // Reset all result state when query is cleared
  useEffect(() => {
    if (query.trim() === '' && molecule) {
      setMolecule(null);
      setSvgContent(null);
      setError(null);
      setResolvedName(null);
      setDidYouMean(null);
      setProjectMatches([]);
      setProjectMatchesLoading(false);
      setReportQuery('');
      setPdfError(null);
      setActiveTab('properties');
      setSearchParams({}, { replace: true });
      sessionStorage.removeItem('discoverant_quick_search_last');
    }
  }, [query]); // eslint-disable-line react-hooks/exhaustive-deps

  const startListening = useCallback(() => {
    if (!SpeechRecognition) return;
    const recognition = new SpeechRecognition();
    recognition.lang = 'en-GB';
    recognition.interimResults = false;
    recognition.maxAlternatives = 1;
    recognitionRef.current = recognition;

    recognition.onresult = async (event: any) => {
      const transcript = event.results[0][0].transcript.trim();
      setQuery(transcript);
      setIsListening(false);
      setDidYouMean(null);

      try {
        const res = await apiClient.get('/search/resolve-name', {
          params: { name: transcript },
        });

        if (res.data.smiles && !res.data.fuzzy) {
          // Exact match — search immediately
          setResolvedName(res.data.name);
          setQuery(res.data.smiles);
          setIsSearching(true);
          setError(null);
          setSvgContent(null);
          try {
            await searchWithSmiles(res.data.smiles);
          } finally {
            setIsSearching(false);
          }
        } else if (res.data.smiles && res.data.fuzzy) {
          // Fuzzy match — show "Did you mean?"
          setDidYouMean({ name: res.data.name, smiles: res.data.smiles });
        } else {
          // No match — try searching the raw text anyway
          handleSearch();
        }
      } catch {
        handleSearch();
      }
    };

    recognition.onerror = () => setIsListening(false);
    recognition.onend = () => setIsListening(false);
    recognition.start();
    setIsListening(true);
    setDidYouMean(null);
  }, [handleSearch, searchWithSmiles]);

  const renderTabContent = () => {
    if (!molecule) return null;
    switch (activeTab) {
      case 'properties':
        return <PropertiesTab molecule={molecule} />;
      case 'patents':
        return <PatentsTab smiles={molecule.smiles} />;
      case 'targets':
        return <TargetsTab smiles={molecule.smiles} />;
      case 'pathways':
        return <PathwaysTab smiles={molecule.smiles} />;
      case 'reactions':
        return <ReactionsTab smiles={molecule.smiles} />;
      case 'bioactivity':
        return <BioactivityTab smiles={molecule.smiles} />;
      case '3d':
        return <ThreeDTab smiles={molecule.smiles} />;
      default:
        return null;
    }
  };

  return (
    <div className="h-full flex flex-col bg-gray-50 overflow-y-auto">
      {/* Search bar */}
      <div className={`flex flex-col items-center ${molecule ? 'pt-8 pb-4' : 'pt-24 pb-8'} transition-all duration-300`}>
        <h1 className={`font-bold text-gray-800 mb-6 transition-all duration-300 ${molecule ? 'text-xl' : 'text-3xl'}`}>
          Quick Search
        </h1>
        <div className="flex items-center gap-3 w-full max-w-2xl px-6">
          <div className="flex-1 relative" ref={suggestionsRef}>
            <input
              type="text"
              value={query}
              onChange={(e) => handleInputChange(e.target.value)}
              onKeyDown={handleKeyDown}
              onFocus={() => { if (suggestions.length > 0) setShowSuggestions(true); }}
              placeholder="Enter SMILES, InChI, or compound name..."
              className="w-full px-5 py-3 pr-10 text-lg border-2 border-gray-300 rounded-xl focus:border-teal-500 focus:ring-2 focus:ring-teal-200 outline-none transition-colors"
              autoFocus
            />
            {query && (
              <button
                onClick={() => { setQuery(''); setSuggestions([]); setShowSuggestions(false); }}
                className="absolute right-3 top-1/2 -translate-y-1/2 w-6 h-6 flex items-center justify-center rounded-full text-gray-400 hover:text-gray-600 hover:bg-gray-100 transition-colors"
                title="Clear search"
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            )}
            {showSuggestions && suggestions.length > 0 && (
              <div className="absolute z-50 top-full left-0 right-0 mt-1 bg-white border border-gray-200 rounded-xl shadow-lg overflow-hidden">
                {suggestions.map((s) => (
                  <button
                    key={s}
                    onClick={() => selectSuggestion(s)}
                    className="w-full text-left px-5 py-2.5 text-sm hover:bg-teal-50 transition-colors capitalize"
                    style={{ color: 'var(--text-primary)' }}
                  >
                    {s}
                  </button>
                ))}
              </div>
            )}
          </div>
          <button
            onClick={() => setDrawEditorOpen(true)}
            className="px-4 py-3 text-lg text-teal-600 bg-white border-2 border-gray-300 rounded-xl hover:border-teal-500 hover:bg-teal-50 transition-colors"
            title="Draw a structure in Ketcher molecular editor"
          >
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.572L16.732 3.732z" />
            </svg>
          </button>
          {SpeechRecognition && (
            <button
              onClick={isListening ? () => { recognitionRef.current?.stop(); setIsListening(false); } : startListening}
              title="Voice search (Beta)"
              style={{
                width: 46, height: 46, borderRadius: 12, border: '2px solid',
                borderColor: isListening ? '#ef4444' : 'var(--card-border)',
                background: isListening ? 'rgba(239, 68, 68, 0.08)' : 'var(--card-bg)',
                color: isListening ? '#ef4444' : '#0d9488',
                cursor: 'pointer', fontSize: 18,
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                transition: 'all 0.2s',
              }}
            >
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round">
                <path d="M12 1a3 3 0 0 0-3 3v8a3 3 0 0 0 6 0V4a3 3 0 0 0-3-3z" />
                <path d="M19 10v2a7 7 0 0 1-14 0v-2" />
                <line x1="12" y1="19" x2="12" y2="23" />
                <line x1="8" y1="23" x2="16" y2="23" />
              </svg>
            </button>
          )}
          <button
            onClick={handleSearch}
            disabled={isSearching || !query.trim()}
            className="px-6 py-3 text-lg font-semibold text-white bg-teal-600 rounded-xl hover:bg-teal-700 disabled:bg-gray-300 disabled:cursor-not-allowed transition-colors"
          >
            {isSearching ? 'Searching...' : 'Search'}
          </button>
        </div>
        {/* Search mode selector */}
        <div style={{ display: 'flex', gap: 4, marginTop: 8 }}>
          {(['substructure', 'exact', 'similarity'] as const).map((mode) => (
            <button
              key={mode}
              onClick={() => setSearchMode(mode)}
              style={{
                padding: '4px 12px',
                fontSize: 12,
                fontWeight: searchMode === mode ? 600 : 400,
                borderRadius: 6,
                border: '1px solid',
                borderColor: searchMode === mode ? '#0d9488' : '#d1d5db',
                background: searchMode === mode ? '#f0fdfa' : 'white',
                color: searchMode === mode ? '#0d9488' : '#6b7280',
                cursor: 'pointer',
                textTransform: 'capitalize',
              }}
            >
              {mode}
            </button>
          ))}
        </div>
        {searchMode === 'similarity' && (
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 6 }}>
            <span style={{ fontSize: 12, color: '#6b7280', whiteSpace: 'nowrap' }}>
              Similarity: {similarityThreshold.toFixed(2)}
            </span>
            <input
              type="range"
              min={0.1}
              max={1.0}
              step={0.05}
              value={similarityThreshold}
              onChange={(e) => setSimilarityThreshold(parseFloat(e.target.value))}
              style={{ width: 160, accentColor: '#0d9488' }}
            />
          </div>
        )}
        {resolvedName && (
          <div className="mt-2 px-4 py-1.5 bg-emerald-50 border border-emerald-200 rounded-lg text-emerald-700 text-sm max-w-2xl">
            Resolved: <strong>{resolvedName}</strong>
          </div>
        )}
        {didYouMean && (
          <div style={{
            display: 'inline-flex', alignItems: 'center', gap: 8,
            padding: '6px 12px', borderRadius: 6, marginTop: 8,
            background: 'rgba(99, 179, 237, 0.08)',
            border: '1px solid rgba(99, 179, 237, 0.2)',
            fontSize: 13,
          }}>
            <span style={{ color: 'var(--text-muted)' }}>Did you mean</span>
            <button
              onClick={async () => {
                const { name, smiles } = didYouMean;
                setDidYouMean(null);
                setResolvedName(name);
                setQuery(smiles);
                setIsSearching(true);
                setError(null);
                setSvgContent(null);
                try {
                  await searchWithSmiles(smiles);
                } finally {
                  setIsSearching(false);
                }
              }}
              style={{
                background: 'rgba(99, 179, 237, 0.15)',
                border: '1px solid rgba(99, 179, 237, 0.3)',
                borderRadius: 4, padding: '2px 8px',
                color: '#63b3ed', fontWeight: 600, fontSize: 13,
                cursor: 'pointer',
              }}
            >
              {didYouMean.name}
            </button>
            <span style={{ color: 'var(--text-muted)' }}>?</span>
            <button
              onClick={() => setDidYouMean(null)}
              style={{
                background: 'none', border: 'none', color: 'var(--text-secondary)',
                cursor: 'pointer', fontSize: 12, padding: '0 4px',
              }}
            >
              ✕
            </button>
          </div>
        )}
        {error && (
          <div className="mt-3 px-4 py-2 bg-red-50 border border-red-200 rounded-lg text-red-600 text-sm max-w-2xl">
            {error}
          </div>
        )}
      </div>

      {/* Results */}
      {molecule && (
        <div className="flex-1 flex flex-col min-h-0 max-w-6xl w-full mx-auto px-6">
          {/* Structure + summary */}
          {svgContent && (
            <div className="flex items-center gap-6 pb-4 border-b border-gray-200">
              <div
                className="w-48 h-32 bg-white border border-gray-200 rounded-lg flex items-center justify-center overflow-hidden cursor-pointer hover:border-teal-400 hover:shadow-md transition-all"
                dangerouslySetInnerHTML={{ __html: svgContent }}
                onClick={() => setEditorOpen(true)}
                title="Click to edit in Ketcher"
              />
              <div className="flex-1 min-w-0">
                {resolvedName && (
                  <p className="text-sm font-semibold text-gray-800 mb-1">{resolvedName}</p>
                )}
                <StructureImage smiles={molecule.smiles} size={150} />
              </div>
              <div className="flex flex-col items-end gap-2">
                <button
                  onClick={downloadPdfSummary}
                  disabled={isDownloadingPdf}
                  className="inline-flex items-center gap-2 px-4 py-2 text-sm font-semibold text-white bg-teal-600 rounded-lg hover:bg-teal-700 disabled:bg-gray-300 disabled:cursor-not-allowed transition-colors"
                  title="Download a calculated compound summary as PDF"
                >
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5l5 5v11a2 2 0 01-2 2z" />
                  </svg>
                  {isDownloadingPdf ? 'Creating PDF...' : 'Download PDF Summary'}
                </button>
                {pdfError && <span className="text-xs text-red-600">{pdfError}</span>}
              </div>
            </div>
          )}

          {/* Project matches banner */}
          <ProjectMatchBanner matches={projectMatches} isLoading={projectMatchesLoading} />

          {/* Knowledge Centre context label */}
          <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginTop: 16, marginBottom: 4 }}>
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="var(--text-muted)" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round">
              <circle cx="12" cy="12" r="10" />
              <line x1="2" y1="12" x2="22" y2="12" />
              <path d="M12 2a15.3 15.3 0 0 1 4 10 15.3 15.3 0 0 1-4 10 15.3 15.3 0 0 1-4-10 15.3 15.3 0 0 1 4-10z" />
            </svg>
            <span style={{ fontSize: 11, fontWeight: 600, letterSpacing: '0.08em', color: 'var(--text-muted)', textTransform: 'uppercase' as const }}>
              Knowledge Centre
            </span>
          </div>

          {/* Tabs */}
          <div className="flex border-b border-gray-200 overflow-x-auto">
            {TABS.map((tab) => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`px-4 py-3 text-sm font-medium whitespace-nowrap transition-colors ${
                  activeTab === tab.id
                    ? 'text-teal-600 border-b-2 border-teal-500'
                    : 'text-gray-500 hover:text-gray-700'
                }`}
              >
                {tab.label}
              </button>
            ))}
          </div>

          {/* Tab content */}
          <div className="flex-1 overflow-y-auto py-4">
            {renderTabContent()}
          </div>
        </div>
      )}

      {/* Empty state */}
      {!molecule && !isSearching && (
        <div className="flex-1 flex flex-col items-center justify-center text-gray-400 -mt-12">
          <svg className="w-16 h-16 mb-4 opacity-30" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
          </svg>
          <p className="text-lg">Enter a SMILES string or compound name to search</p>
          <p className="text-sm mt-2">Examples: c1ccc(-c2ccccc2)cc1 (biphenyl) or "aspirin"</p>
        </div>
      )}

      {/* Structure editor modal — view/edit existing structure */}
      {molecule && (
        <StructureEditorModal
          isOpen={editorOpen}
          onClose={() => setEditorOpen(false)}
          initialStructure={molecule.smiles || ''}
          title={resolvedName || 'View Structure'}
        />
      )}

      {/* Draw structure modal — draw from scratch then search */}
      <StructureEditorModal
        isOpen={drawEditorOpen}
        onClose={() => setDrawEditorOpen(false)}
        onSave={handleDrawSave}
        initialStructure=""
        title="Draw Search Structure"
      />
    </div>
  );
};

export default QuickSearchPage;
